import React from 'react';
import { ExpenseCategory, EXPENSE_CATEGORY_INFO } from '@/types/expense';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ExpenseCategorySelectorProps {
  value?: ExpenseCategory;
  onValueChange: (value: ExpenseCategory) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  allowedCategories?: ExpenseCategory[]; // Optional filter for country-specific categories
}

const ExpenseCategorySelector: React.FC<ExpenseCategorySelectorProps> = ({
  value,
  onValueChange,
  placeholder = "Select category...",
  required = false,
  disabled = false,
  className,
  allowedCategories
}) => {
  // Filter categories based on allowed categories if provided
  const categories = Object.values(EXPENSE_CATEGORY_INFO).filter(category => 
    !allowedCategories || allowedCategories.includes(category.value)
  );

  return (
    <TooltipProvider>
      <Select 
        value={value} 
        onValueChange={onValueChange}
        required={required}
        disabled={disabled}
      >
        <SelectTrigger className={className} data-testid="expense-category-select">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {categories.map((category) => (
            <Tooltip key={category.value} delayDuration={300}>
              <TooltipTrigger asChild>
                <SelectItem value={category.value} className="cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="text-base" aria-hidden="true">
                      {category.icon}
                    </span>
                    <span>{category.label}</span>
                  </div>
                </SelectItem>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <div className="space-y-2">
                  <p className="font-medium">{category.description}</p>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Examples:
                    </p>
                    <ul className="text-xs text-muted-foreground">
                      {category.examples.map((example, index) => (
                        <li key={index}>• {example}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          ))}
        </SelectContent>
      </Select>
    </TooltipProvider>
  );
};

export default ExpenseCategorySelector; 